import { mapValues } from "lodash-es";

import type { Status } from "@/backend/natsstubs/almsync/common/models";
import type {
  Art,
  BoardIteration as ApiBoardIteration,
  GetBoardIterationsResponse,
  GetBoardsResponse,
  GetGroupBoardIdsResponse,
  Link,
  SessionSummary as ApiSession,
  Sticky as ApiSessionSticky,
  TimerEvent,
} from "@/backend/natsstubs/session/models";
import type { StickyType as ApiStickyType } from "@/backend/natsstubs/sticky_type/models";
import { mapApiShape } from "@/backend/read/mapApiData";
import type { SessionAlmStatus } from "@/model/session";
import type { ServerTime } from "@/model/timer";

import type {
  ServerArt,
  ServerBoard,
  ServerBoardIteration,
  ServerEvent,
  ServerFlexBoard,
  ServerLink,
  ServerSearchResult,
  ServerStickyType,
  ServerSwimlane,
  ServerSession,
  ServerIteration,
  ServerStickyGroupBoardIds,
} from "../serverModel";

type ApiBoard = GetBoardsResponse["boards"][number];

/**
 * Maps a generated session `Board` union member to the legacy
 * `ServerBoard`/`ServerFlexBoard` shape. `getBoards` carries the initial shape
 * snapshot; live subscriptions apply subsequent shape changes. Legacy `user_id`
 * is the board's TEAM id (mapBoard derives `board.team` from it) — the stub
 * carries it as `teamId`.
 */
export function mapApiBoardInfo(board: ApiBoard): ServerBoard | ServerFlexBoard {
  if (board.boardType === "flex") {
    return {
      board_id: board.id,
      board_type: "flex",
      name: board.name,
      flexboard_type: board.flexboardType,
      ...(board.shapes !== undefined && { shapes: board.shapes.map(mapApiShape) }),
    };
  }
  return {
    board_id: board.id,
    board_type: board.boardType,
    team_name: ("teamName" in board ? board.teamName : undefined) ?? "",
    user_id: board.boardType === "team" ? board.teamId : null,
    ...(board.shapes !== undefined && { shapes: board.shapes.map(mapApiShape) }),
    ...("artId" in board && board.artId !== undefined && { art_id: board.artId }),
    ...(board.boardType === "program" &&
      board.swimlanes !== undefined && {
        swimlanes: board.swimlanes.map(mapApiSwimlane),
      }),
  };
}

function mapApiSwimlane(swimlane: {
  type: string;
  name?: string;
  teamIds?: number[];
}): ServerSwimlane {
  return {
    type: swimlane.type as ServerSwimlane["type"],
    ...(swimlane.name !== undefined && { name: swimlane.name }),
    ...(swimlane.teamIds !== undefined && { team_ids: swimlane.teamIds }),
  };
}

export function mapApiArt(art: Art): ServerArt {
  return {
    id: art.id,
    name: art.name,
    teams: art.teams.map((team) => ({
      id: team.id,
      name: team.name,
      external: team.external ?? false,
    })),
  };
}

export function mapApiLink(link: Link): ServerLink {
  return {
    id: link.id,
    from_sticky_id: link.fromStickyId,
    to_sticky_id: link.toStickyId,
  };
}

/**
 * The stub's `Status` also carries `synced`/`syncing`, which the legacy
 * `SessionAlmStatus` folds into `running`.
 */
export function mapApiAlmStatus(status: Status): SessionAlmStatus {
  return status === "synced" || status === "syncing" ? "running" : status;
}

/**
 * The stub only serves timer events, so `type` is a constant discriminator;
 * `mapEvents` still filters on it before building the frontend model.
 */
export function mapApiTimerEvent(event: TimerEvent, sessionId: string): ServerEvent {
  return {
    id: event.id,
    session_id: sessionId,
    ...(event.boardId !== undefined && event.boardId !== null && { board_id: event.boardId }),
    ...(event.artId !== undefined && event.artId !== null && { art_id: event.artId }),
    type: "timer",
    created_by: event.createdBy,
    created_at: event.createdAt,
    updated_by: event.updatedBy,
    updated_at: event.updatedAt,
    data: {
      name: event.data.name,
      state: event.data.state,
      duration: event.data.duration,
      end: event.data.end as ServerTime,
    },
  };
}

/** `sessions.get` answers the legacy `session.list` summaries the session picker consumes. */
export function mapApiSession(session: ApiSession): ServerSession {
  return {
    session_id: session.id,
    name: session.name,
    creation_date: session.creationDate,
    archived: session.archived,
    planning_dates: session.planningDates ?? null,
    iterations: (session.iterations ?? []).map((iteration): ServerIteration => ({
      id: iteration.id,
      name: iteration.name,
      start: iteration.start,
      end: iteration.end,
    })),
  };
}
/** Legacy board_iteration.list shape: iterations keyed by team board id. */
export function mapApiBoardIterations(
  response: GetBoardIterationsResponse,
): Record<string, ServerBoardIteration[]> {
  return mapValues(response.iterationsByBoard, (iterations) =>
    iterations.map((iteration: ApiBoardIteration) => ({
      board_iteration_id: iteration.boardIterationId,
      // legacy data sometimes stores the velocity as a string
      velocity: Number(iteration.velocity),
      ...(iteration.statusDistribution !== undefined && {
        status_distribution: iteration.statusDistribution,
      }),
    })),
  );
}

export function mapApiSearchResult(sticky: ApiSessionSticky): ServerSearchResult {
  return {
    id: sticky.id,
    board_id: sticky.boardId,
    type_id: sticky.typeId,
    text: sticky.text ?? "",
    objectives: (sticky.objectives ?? []).map((objective) => ({
      id: objective.id,
      boardId: objective.boardId,
    })),
    ...(sticky.almIssueId !== undefined && { alm_issue_id: String(sticky.almIssueId) }),
    ...(sticky.almIssueUrl !== undefined && { alm_issue_url: sticky.almIssueUrl }),
    ...(sticky.flagType !== undefined && { flag_type: sticky.flagType }),
    ...(present(sticky.teamId) && { team_id: sticky.teamId }),
    ...(present(sticky.artId) && { art_id: String(sticky.artId) }),
    ...(present(sticky.stickyGroup) && { sticky_group: sticky.stickyGroup }),
    ...(present(sticky.almSource) && { alm_source: sticky.almSource }),
    ...(present(sticky.status) && { status: sticky.status }),
    ...(sticky.statusClass !== undefined && { status_class: sticky.statusClass }),
    ...(present(sticky.iterationNumber) && { iteration_number: sticky.iterationNumber }),
    ...(present(sticky.dependTeamId) && { depend_team_id: sticky.dependTeamId }),
    ...(present(sticky.precondTeamId) && { precond_team_id: sticky.precondTeamId }),
    ...(present(sticky.storyPoints) && { story_points: sticky.storyPoints }),
    ...(sticky.WSJFValue !== undefined && { WSJF_value: sticky.WSJFValue }),
  };
}

export function mapApiStickyType(stickyType: ApiStickyType): ServerStickyType {
  return {
    id: stickyType.id,
    name: stickyType.name,
    alm_type: stickyType.almType ?? null,
    color: stickyType.color,
    functionality: stickyType.functionality,
    origin_board_type: stickyType.originBoardType,
    available_board_types: stickyType.availableBoardTypes,
    ...(present(stickyType.altColor) && { alt_color: stickyType.altColor }),
    ...(stickyType.originFlexboardType !== undefined && {
      origin_flexboard_type: stickyType.originFlexboardType,
    }),
    ...(stickyType.availableFlexboardTypes !== undefined && {
      available_flexboard_types: stickyType.availableFlexboardTypes,
    }),
    ...(stickyType.stepEstimate !== undefined && { step_estimate: stickyType.stepEstimate }),
    ...(stickyType.stepEstimateValues !== undefined && {
      step_estimate_values: stickyType.stepEstimateValues,
    }),
    ...(stickyType.index !== undefined && { index: stickyType.index }),
  };
}

function present<T>(value: T | null | undefined): value is T {
  return value !== undefined && value !== null;
}

/** `group_boards.get` answers the boards a grouped sticky spans, in the legacy shape. */
export function mapApiGroupBoards(response: GetGroupBoardIdsResponse): ServerStickyGroupBoardIds {
  return {
    board_ids: response.boardIds,
    stickies: response.stickies.map((sticky) => ({
      board_id: sticky.boardId,
      sticky_id: sticky.stickyId,
    })),
  };
}
